import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <img 
            src="/nightbuzz-logo.png" 
            alt="NightBuZz" 
            className="h-10 cursor-pointer"
            onClick={() => navigate('/')}
          />
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Space Grotesk' }}>
          Terms of Service
        </h1>
        
        <div className="prose prose-invert max-w-none space-y-6 text-slate-300">
          <p className="text-sm text-slate-400">Last Updated: March 2025</p>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using NightBuZz ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">2. Description of Service</h2>
            <p>
              NightBuZz is a mobile-first platform that connects nightlife patrons with local bars and venues. 
              Our Service includes but is not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Real-time bar discovery and crowd information</li>
              <li>Check-in functionality with friends</li>
              <li>Video sharing of venue atmospheres</li>
              <li>QR code-based discounts and promotions</li>
              <li>Bar owner dashboard and analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">3. User Accounts</h2>
            <p>
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Be at least 21 years of age to use the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">4. User Conduct</h2>
            <p>You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Infringe on any third party's intellectual property rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">5. Intellectual Property</h2>
            <p>
              The NightBuZz name, logo, and all related marks, logos, and designs are trademarks of NightBuZz. 
              All content, features, and functionality of the Service are owned by NightBuZz and are protected 
              by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="mt-4">
              You may not copy, modify, distribute, sell, or lease any part of our Service or included software, 
              nor may you reverse engineer or attempt to extract the source code of that software.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">6. User Content</h2>
            <p>
              By posting content on NightBuZz, you grant us a non-exclusive, worldwide, royalty-free license 
              to use, display, reproduce, and distribute your content in connection with the Service.
            </p>
            <p className="mt-4">
              You retain ownership of your content and are responsible for ensuring you have the right to post it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">7. Bar Owner Terms</h2>
            <p>
              Bar owners who register for business accounts agree to additional terms:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate business information</li>
              <li>Honor all discounts and promotions posted on the platform</li>
              <li>Comply with all applicable local laws and regulations</li>
              <li>Pay all applicable subscription fees in a timely manner</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">8. Payments and Subscriptions</h2>
            <p>
              Certain features require payment. By subscribing to paid services, you agree to pay all 
              applicable fees. Payments are processed securely through Stripe. Subscription fees are 
              non-refundable except as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">9. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. NIGHTBUZZ DOES NOT GUARANTEE 
              THE ACCURACY OF CROWD LEVELS, WAIT TIMES, OR OTHER USER-GENERATED INFORMATION. USE THE SERVICE 
              AT YOUR OWN RISK.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">10. Limitation of Liability</h2>
            <p>
              NIGHTBUZZ SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR 
              PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">11. Termination</h2>
            <p>
              We may terminate or suspend your account at any time for violations of these Terms. 
              Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of material 
              changes via email or through the Service. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mt-8 mb-4">13. Contact</h2>
            <p>
              For questions about these Terms, contact us at: <br />
              <a href="mailto:legal@getnightbuzz.com" className="text-purple-400 hover:text-purple-300">
                legal@getnightbuzz.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
